import { useTimeline } from '../../api/hooks/useObservacoes';
import type { TimelineItem } from '../../api/hooks/useObservacoes';
import { formatDateBR } from '../../utils/dateUtils';

interface TimelineAlunoProps {
  alunoId: string;
  limit?: number;
}

/**
 * Timeline unificada do aluno (Fase 4).
 * Consolida: presença + notas + observações + justificativas.
 */
export function TimelineAluno({ alunoId, limit = 20 }: TimelineAlunoProps) {
  const { data: items = [], isLoading } = useTimeline(alunoId, limit);

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted, #999)' }}>Carregando timeline...</div>;
  }

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted, #999)' }}>
        <p style={{ margin: 0 }}>Nenhum evento registrado</p>
      </div>
    );
  }

  // Agrupar por data
  const grouped: Record<string, TimelineItem[]> = {};
  for (const item of items) {
    const dateKey = formatDateBR(item.data);
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(item);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {Object.entries(grouped).map(([dateLabel, dayItems]) => (
        <div key={dateLabel}>
          {/* Data header */}
          <div style={{
            fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #888)',
            padding: '12px 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            {dateLabel}
          </div>

          {/* Items do dia */}
          {dayItems.map((item, idx) => (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '10px 0',
              borderBottom: idx < dayItems.length - 1 ? '1px solid var(--border-color, #f0f0f0)' : 'none',
            }}>
              {/* Linha vertical + ícone */}
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32,
              }}>
                <span style={{ fontSize: '1.1rem' }}>{item.icone}</span>
                {idx < dayItems.length - 1 && (
                  <div style={{ width: 2, flex: 1, background: 'var(--border-color, #e5e7eb)', minHeight: 12 }} />
                )}
              </div>

              {/* Conteúdo */}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 500, color: 'var(--text-main, #111)' }}>
                  {item.descricao}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                  <span style={{
                    fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4,
                    background: `${item.cor}15`, color: item.cor, fontWeight: 600,
                  }}>
                    {item.tipo}
                  </span>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted, #aaa)' }}>
                    {new Date(item.data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
